<template>
  <BaseTemplate app-name="Whatsapp" page-title="">
    <template #content>
      <div class="min-h-screen bg-gray-50 dark:bg-gray-900">
        <!-- Header with back button and ticket info -->
        <div class="bg-white dark:bg-gray-800 px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center sticky top-0 z-10 shadow-sm">
          <button 
            @click="close" 
            class="flex items-center gap-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <ChevronLeft class="w-5 h-5" />
            <span>Voltar</span>
          </button>
          <h2 class="text-lg font-bold text-gray-900 dark:text-white ml-4 truncate">
            #{{ ticket?.id }} - {{ ticket?.titulo }}
          </h2>
          
        </div>

        <!-- Loading and Error states -->
        <div v-if="loading" class="flex flex-col items-center justify-center min-h-[400px]">
          <Loader2 class="w-8 h-8 text-blue-600 animate-spin" />
          <p class="mt-2 text-gray-600 dark:text-gray-300">Carregando chamado...</p>
        </div>
        
        <div v-if="error" class="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg text-red-600 dark:text-red-400 max-w-md mx-auto my-8">
          <AlertCircle class="w-6 h-6 mb-2 mx-auto" />
          <p class="text-center">{{ error }}</p>
        </div>
        
        <!-- Main Content Layout -->
        <div v-if="ticket && !loading" class="container mx-auto px-4 py-6">
          <!-- Top Section: Details (Left) + Actions (Right) -->
          <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            <!-- Detalhes do Ticket (2/3 da largura) -->
            <div class="lg:col-span-2">
              <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <!-- Header com badges -->
                <div class="flex flex-wrap items-center gap-3 mb-6 pb-4 border-b border-gray-200 dark:border-gray-700">
                  <span 
                    :class="getPriorityClass(ticket.prioridade)"
                    class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium"
                  >
                    {{ formatPriority(ticket.prioridade) }}
                  </span>
                  <span 
                    :class="getStatusClass(ticket.status)"
                    class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium"
                  >
                    {{ formatStatus(ticket.status) }}
                  </span>
                </div>

                <!-- Informações básicas em grid -->
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div class="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
                    <span class="text-sm font-medium text-gray-500 dark:text-gray-400">Requisitado por</span>
                    <p class="text-base font-semibold text-gray-900 dark:text-white mt-1">{{ ticket.criado_por }}</p>
                  </div>
                  <div class="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
                    <span class="text-sm font-medium text-gray-500 dark:text-gray-400">Criado em</span>
                    <p class="text-base font-semibold text-gray-900 dark:text-white mt-1">{{ formatDate(ticket.data_criacao) }}</p>
                  </div>
                  <div class="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
                    <span class="text-sm font-medium text-gray-500 dark:text-gray-400">Fila</span>
                    <p class="text-base font-semibold text-gray-900 dark:text-white mt-1">{{ ticket.fila?.nome || 'Não definida' }}</p>
                  </div>
                  <div class="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
                    <span class="text-sm font-medium text-gray-500 dark:text-gray-400">Tipo</span>
                    <p class="text-base font-semibold text-gray-900 dark:text-white mt-1">{{ ticket.tipo || 'Não definido' }}</p>
                  </div>
                  <div class="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
                    <span class="text-sm font-medium text-gray-500 dark:text-gray-400">Unidade</span>
                    <p class="text-base font-semibold text-gray-900 dark:text-white mt-1">
                      {{ unidades.find(u => u.id === ticket.unidade)?.nome || 'Não definida' }}
                    </p>
                  </div>
                  <div class="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
                    <span class="text-sm font-medium text-gray-500 dark:text-gray-400">Atribuído para</span>
                    <p class="text-base font-semibold text-gray-900 dark:text-white mt-1">{{ ticket.analista || 'Não atribuído' }}</p>
                  </div>
                </div>

                <!-- Descrição -->
                <div class="mb-6">
                  <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-3">Descrição do Problema</h3>
                  <div class="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                    <p class="text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed">{{ ticket.descricao }}</p>
                  </div>
                </div>

                <!-- Anexo de imagem -->
                <div v-if="ticket.imagem_base64" class="mb-6">
                  <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-3">Anexo</h3>
                  <div class="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                    <img
                      :src="`data:image/png;base64,${ticket.imagem_base64}`"
                      alt="Anexo do Chamado"
                      class="max-w-full h-auto rounded-lg shadow-sm border border-gray-200 dark:border-gray-700"
                    />
                  </div>
                </div>

                <!-- Contexto do chat -->
                <div v-if="ticket.contexto_chat" class="mb-6">
                  <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-3">Contexto do Chat</h3>
                  <div class="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 overflow-x-auto">
                    <pre class="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{{ ticket.contexto_chat }}</pre>
                  </div>
                </div>

                <!-- SLA Info (apenas para usuários TI) -->
                <div v-if="isTIUser" class="border-t border-gray-200 dark:border-gray-700 pt-6">
                  <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">SLA</h3>
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div class="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                      <div class="flex justify-between items-center">
                        <span class="text-sm text-gray-600 dark:text-gray-400">Tempo de resposta:</span>
                        <span :class="getSlaClass(ticket.sla_status)" class="text-sm font-medium">
                          {{ ticket.sla_time || 'N/A' }}
                        </span>
                      </div>
                    </div>
                    <div class="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                      <div class="flex justify-between items-center">
                        <span class="text-sm text-gray-600 dark:text-gray-400">Tempo total:</span>
                        <span class="text-sm font-medium text-gray-900 dark:text-white">
                          {{ ticket.status === 'resolvido' ? ticket.total_time : 'Em aberto' }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Ações (1/3 da largura - lado direito) -->
            <div v-if="isTIUser" class="lg:col-span-1">
              <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 sticky top-24">
                <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-6 pb-3 border-b border-gray-200 dark:border-gray-700">
                  Ações
                </h3>
                
                <div class="space-y-5">
                  <!-- Prioridade -->
                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Prioridade
                    </label>
                    <div class="relative">
                      <select 
                        v-model="ticket.prioridade"
                        @change="updateTicketPriority"
                        class="w-full pl-3 pr-8 py-2.5 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none text-sm"
                      >
                        <option value="baixa">Baixa</option>
                        <option value="media">Média</option>
                        <option value="alta">Alta</option>
                      </select>
                      <ChevronDown class="absolute right-2.5 top-3 w-4 h-4 text-gray-400 pointer-events-none" />
                    </div>
                  </div>
                  
                  <!-- Fila -->
                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Fila
                    </label>
                    <div class="relative">
                      <select
                        v-model="selectedQueue"
                        @change="onQueueChange"
                        class="w-full pl-3 pr-8 py-2.5 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none text-sm"
                      >
                        <option value="">Selecione a fila</option>
                        <option v-for="fila in filas" :key="fila.id" :value="fila.nome">{{ fila.nome }}</option>
                      </select>
                      <ChevronDown class="absolute right-2.5 top-3 w-4 h-4 text-gray-400 pointer-events-none" />
                    </div>
                  </div>
                  
                  <!-- Tipo de Chamado -->
                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Tipo de Chamado
                    </label>
                    <div class="relative">
                      <select
                        v-model="ticket.tipo"
                        @change="updateTicketTipo"
                        class="w-full pl-3 pr-8 py-2.5 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none text-sm"
                      >
                        <option value="">Selecione o tipo</option>
                        <option v-for="tipo in tiposChamado" :key="tipo.id" :value="tipo.nome">{{ tipo.nome }}</option>
                      </select>
                      <ChevronDown class="absolute right-2.5 top-3 w-4 h-4 text-gray-400 pointer-events-none" />
                    </div>
                  </div>
                  
                  <!-- Responsável -->
                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Atribuir para
                    </label>
                    <div class="relative">
                      <select
                        v-model="ticket.analista"
                        @change="updateTicketAssignee"
                        :disabled="!ticket.fila"
                        class="w-full pl-3 pr-8 py-2.5 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <option :value="null">Não atribuído</option>
                        <option v-for="user in assignableUsers" :key="user.id" :value="user.nome">{{ user.nome }}</option>
                      </select>
                      <ChevronDown class="absolute right-2.5 top-3 w-4 h-4 text-gray-400 pointer-events-none" />
                    </div>
                  </div>
                  
                  <!-- Status -->
                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Status
                    </label>
                    <div class="relative">
                      <select
                        v-model="ticket.status"
                        @change="updateTicketStatus"
                        :disabled="!ticket.analista"
                        class="w-full pl-3 pr-8 py-2.5 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <option value="nao-atribuido">Não Atribuído</option>
                        <option value="em-andamento">Em Andamento</option>
                        <option value="resolvido">Resolvido</option>
                      </select>
                      <ChevronDown class="absolute right-2.5 top-3 w-4 h-4 text-gray-400 pointer-events-none" />
                    </div>
                  </div>
                                             <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-6 pb-3 border-b border-gray-200 dark:border-gray-700">
              Interações
            </h3>
            
            <!-- Lista de comentários -->
            <div class="mb-6">
              <div v-if="comments.length === 0" class="text-center py-12">
                <MessageSquare class="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                <p class="text-gray-500 dark:text-gray-400 text-lg">Nenhuma interação registrada</p>
                <p class="text-gray-400 dark:text-gray-500 text-sm mt-1">Seja o primeiro a comentar neste chamado</p>
              </div>
              
              <div v-for="comment in comments" :key="comment.id" class="mb-6 last:mb-0">
                <div class="flex space-x-4">
                  <div class="flex-shrink-0">
                    <div class="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                      <span class="text-sm font-medium text-blue-600 dark:text-blue-400">{{ comment.autor.charAt(0) }}</span>
                    </div>
                  </div>
                  <div class="flex-1">
                    <div class="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                      <div class="flex items-center justify-between mb-3">
                        <span class="text-sm font-semibold text-gray-900 dark:text-white">{{ comment.autor }}</span>
                        <span class="text-xs text-gray-500 dark:text-gray-400">{{ comment.data }}</span>
                      </div>
                      <p class="text-gray-700 dark:text-gray-300 leading-relaxed">{{ comment.texto }}</p>
                      <div v-if="comment.imagem_base64" class="mt-4">
                        <img
                          :src="`data:image/png;base64,${comment.imagem_base64}`"
                          class="max-w-full rounded-lg shadow-sm border border-gray-200 dark:border-gray-700"
                          alt="Anexo"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Formulário para adicionar comentário -->
            <div class="border-t border-gray-200 dark:border-gray-700 pt-6">
              <form @submit.prevent="addComment" class="space-y-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Adicionar comentário
                  </label>
                  <textarea 
                    v-model="newComment.content"
                    placeholder="Digite seu comentário aqui..."
                    rows="4"
                    class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none transition-all"
                    required
                  ></textarea>
                </div>
                
                <div class="flex items-center justify-between">
                  <div class="flex items-center space-x-3">
                    <label class="cursor-pointer inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors">
                      <Paperclip class="w-4 h-4 mr-2" />
                      Anexar arquivo
                      <input type="file" class="sr-only" multiple @change="handleCommentFileUpload" />
                    </label>
                    <div v-if="newComment.attachments.length > 0" class="text-sm text-gray-500 dark:text-gray-400">
                      {{ newComment.attachments.length }} arquivo(s) selecionado(s)
                    </div>
                  </div>
                  <button 
                    type="submit"
                    class="px-6 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  >
                    Comentar
                  </button>
                </div>
              </form>
            </div>
          </div>

                </div>               
              </div>
            </div>
          </div>

          <!-- Bottom Section: Interações -->

        </div>
      </div>
    </template>
  </BaseTemplate>
</template>

<script setup>
import { ref, onMounted, computed, defineProps } from 'vue';
import { useRouter } from 'vue-router';
import { 
  Paperclip, 
  ChevronDown, 
  ChevronLeft, 
  AlertCircle, 
  Loader2,
  MessageSquare
} from 'lucide-vue-next';
import BaseTemplate from '../templates/BaseTemplate.vue';
import apiClient from '@/services/authService';
import { useUserStore } from '@/stores/userStore';
import { useFilasStore } from '@/stores/tickets/filasStore';

// Props
const props = defineProps({
  id: {
    type: [String, Number],
    required: true,
  },
});

// Composables
const router = useRouter();
const userStore = useUserStore();
const filasStore = useFilasStore();

// Reactive data
const ticket = ref(null);
const loading = ref(true);
const error = ref(null);
const comments = ref([]);
const filas = computed(() => filasStore.filas);
const tiposChamado = ref([]);
const assignableUsers = ref([]);
const unidades = ref([]);
const selectedQueue = ref('');
const isTIUser = computed(() => userStore.grupos && userStore.grupos.includes('ti'));

const newComment = ref({
  content: '',
  attachments: []
});

// Methods
const close = () => {
  router.push({ name: 'GestaoChamados' });
};

const formatDate = (isoString) => {
  if (!isoString) return '';
  const dt = new Date(isoString);
  return dt.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
};

const getPriorityClass = (priority) => {
  const classes = {
    'baixa': 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
    'media': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
    'alta': 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
  };
  return classes[priority] || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
};

const getStatusClass = (status) => {
  const classes = {
    'nao-atribuido': 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
    'em-andamento': 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
    'resolvido': 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
  };
  return classes[status] || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
};

const getSlaClass = (slaStatus) => {
  const classes = {
    'ok': 'text-green-600 dark:text-green-400',
    'warning': 'text-yellow-600 dark:text-yellow-400',
    'critical': 'text-red-600 dark:text-red-400'
  };
  return classes[slaStatus] || 'text-gray-600 dark:text-gray-400';
};

const priorityLabels = {
  baixa: 'Baixa',
  media: 'Média',
  alta: 'Alta',
};

const statusLabels = {
  'nao-atribuido': 'Não Atribuído',
  'em-andamento': 'Em Andamento',
  resolvido: 'Resolvido',
};

const formatPriority = key => priorityLabels[key] || key;
const formatStatus = key => statusLabels[key] || key;

const handleCommentFileUpload = (event) => {
  const files = Array.from(event.target.files);
  newComment.value.attachments.push(...files);
};

const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(',')[1]);
    reader.onerror = error => reject(error);
    reader.readAsDataURL(file);
  });
};

const updateTicketFields = async (fields) => {
  if (!ticket.value) return;
  
  try {
    const payload = {
      titulo: ticket.value.titulo,
      descricao: ticket.value.descricao,
      sistema: ticket.value.sistema,
      tipo: fields.tipo !== undefined ? fields.tipo : ticket.value.tipo,
      prioridade: ticket.value.prioridade,
      imagem_base64: ticket.value.imagem_base64 || '',
      status: ticket.value.status,
      fila: ticket.value.fila?.nome || '',
      analista: ticket.value.analista_id,
      unidade: fields.unidade_id !== undefined ? fields.unidade_id : ticket.value.unidade,
    };

    if (fields.status !== undefined) payload.status = fields.status;
    if (fields.fila !== undefined) payload.fila = fields.fila;
    if (fields.priority !== undefined) payload.prioridade = fields.priority;
    if (fields.analista !== undefined) payload.analista = fields.analista;
    
    const response = await apiClient.put(
      `/api/v1/tickets/chamados/${ticket.value.id}`,
      payload
    );
    
    const updated = response.data;
    
    Object.assign(ticket.value, {
      status: updated.status,
      fila: updated.fila,
      prioridade: updated.prioridade,
      analista: updated.analista,
      analista_id: updated.analista_id,
      unidade: updated.unidade,
      tipo: updated.tipo,
      sla_status: updated.sla_status,
      sla_time: updated.sla_time,
      total_time: updated.total_time
    });
    
  } catch (e) {
    console.error('Erro ao atualizar ticket:', e);
  }
};

const updateTicketStatus = () => updateTicketFields({ status: ticket.value.status });
const updateTicketPriority = () => updateTicketFields({ priority: ticket.value.prioridade });
const updateTicketTipo = () => updateTicketFields({ tipo: ticket.value.tipo });

const updateTicketAssignee = async () => {
  if (!ticket.value) return;

  let analistaIdToSend = null;
  if (ticket.value.analista) {
    const foundAnalyst = assignableUsers.value.find(a => a.nome === ticket.value.analista);
    analistaIdToSend = foundAnalyst ? foundAnalyst.id : null;
  }

  await updateTicketFields({ analista: analistaIdToSend });
};

const onQueueChange = async () => {
  if (!ticket.value) return;
  
  ticket.value.fila = { nome: selectedQueue.value };
  ticket.value.analista = null;
  ticket.value.status = 'nao-atribuido';

  await updateTicketFields({
    fila: selectedQueue.value,
    status: ticket.value.status,
    analista: null
  });

  await fetchAssignableUsersForQueue(selectedQueue.value);
};

const fetchAssignableUsersForQueue = async (queueName) => {
  const filaObj = filas.value.find(f => f.nome === queueName);
  if (filaObj) {
    try {
      const response = await apiClient.get(`/api/v1/tickets/fila-users/?fila_id=${filaObj.id}`);
      assignableUsers.value = response.data;
    } catch (error) {
      console.error(`Erro ao buscar usuários para a fila '${queueName}':`, error);
      assignableUsers.value = [];
    }
  } else {
    assignableUsers.value = [];
  }
};

const addComment = async () => {
  if (!ticket.value || !newComment.value.content.trim()) return;

  let imagem = null;
  if (newComment.value.attachments.length > 0) {
    const file = newComment.value.attachments[0];
    if (file.type.startsWith('image/')) {
      imagem = await fileToBase64(file);
    }
  }

  const payload = {
    texto: newComment.value.content,
    imagem_base64: imagem
  };

  try {
    const res = await apiClient.post(
      `/api/v1/tickets/chamados/${ticket.value.id}/comentarios`,
      payload
    );
    const c = res.data;

    comments.value.push({
      id: c.id,
      autor: c.autor,
      texto: c.texto,
      data: formatDate(c.data),
      imagem_base64: c.imagem_base64
    });

    ticket.value.sla_status = 'ok';
    newComment.value = { content: '', attachments: [] };
    
  } catch (e) {
    console.error('Erro ao adicionar comentário:', e);
  }
};

// Lifecycle
onMounted(async () => {
  try {
    if (filasStore.filas.length === 0) {
      await filasStore.loadFilas();
    }
    
    const tiposRes = await apiClient.get('/api/v1/tickets/tipos/');
    tiposChamado.value = tiposRes.data;
    
    const unidadesRes = await apiClient.get('/api/v1/enterprises/');
    unidades.value = unidadesRes.data;
    
    const response = await apiClient.get(`/api/v1/tickets/chamados/${props.id}`);
    ticket.value = response.data;
    
    if (ticket.value.fila) {
      selectedQueue.value = ticket.value.fila.nome;
      await fetchAssignableUsersForQueue(selectedQueue.value);
    }
    
    const commentsRes = await apiClient.get(`/api/v1/tickets/chamados/${props.id}/comentarios`);
    comments.value = commentsRes.data.map(c => ({
      id: c.id,
      autor: c.autor,
      texto: c.texto,
      data: formatDate(c.data),
      imagem_base64: c.imagem_base64
    }));
    
  } catch (err) {
    console.error("Erro ao buscar detalhes do chamado:", err);
    error.value = "Não foi possível carregar o chamado.";
  } finally {
    loading.value = false;
  }
});
</script>
